import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { DownloadSimple, ArrowsClockwise } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const STATUSES = [
    { v: '', l: 'všechny' },
    { v: 'answered', l: 'answered' },
    { v: 'missed', l: 'missed' },
    { v: 'failed', l: 'failed' },
    { v: 'busy', l: 'busy' },
    { v: 'in_progress', l: 'in_progress' },
];

const CallLogs = () => {
    const [items, setItems] = useState([]);
    const [number, setNumber] = useState('');
    const [status, setStatus] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const { toast } = useToast();

    const load = async () => {
        const params = {};
        if (number) params.number = number;
        if (status) params.status = status;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const r = await api.get('/call-logs', { params });
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const sync = async () => {
        try {
            const r = await api.post('/didww/cdrs/sync');
            toast({ title: 'CDR Sync', description: `Inserted ${r.data.inserted}` });
            load();
        } catch (e) {
            toast({ title: 'Sync selhal', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const exportCsv = () => {
        const token = localStorage.getItem('vg_token');
        const qs = new URLSearchParams();
        if (number) qs.set('number', number);
        if (status) qs.set('status', status);
        if (dateFrom) qs.set('date_from', dateFrom);
        if (dateTo) qs.set('date_to', dateTo);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/call-logs/export.csv?${qs.toString()}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(b);
                a.download = 'call_logs.csv';
                a.click();
            });
    };

    return (
        <>
            <PageHeader
                eyebrow="Telephony"
                title="Call Logy (CDR)"
                description="Detailní záznam příchozích i odchozích hovorů, s časem, délkou, statusem a cenou."
                actions={
                    <div className="flex gap-2">
                        <button onClick={sync} data-testid={TID.didwwSyncCdrs}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border hover:bg-[var(--bg-surface-hover)]"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <ArrowsClockwise size={14} weight="bold" />Sync DIDWW
                        </button>
                        <button onClick={exportCsv} data-testid={TID.cdrExport}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            <DownloadSimple size={14} weight="bold" />Export CSV
                        </button>
                    </div>
                }
            />

            <Card>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                    <input value={number} onChange={(e) => setNumber(e.target.value)}
                           placeholder="Číslo (DID / caller)"
                           className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                            className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                           className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                           className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                    <button onClick={load}
                            className="px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        Filtrovat
                    </button>
                </div>

                <div data-testid={TID.cdrTable}>
                    {items.length === 0 ? (
                        <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                            Žádné hovory neodpovídají filtru.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.1em]"
                                    style={{ color: 'var(--text-secondary)' }}>
                                    <th className="text-left py-2">Čas</th>
                                    <th className="text-left">Směr</th>
                                    <th className="text-left">DID</th>
                                    <th className="text-left">Volající</th>
                                    <th className="text-left">Cíl</th>
                                    <th className="text-left">AI Intent</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-right">Délka</th>
                                    <th className="text-right">Cena</th>
                                    <th className="text-left">Lead</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr key={c.id} className="border-t"
                                        style={{ borderColor: 'var(--border-default)' }}>
                                        <td className="py-2 font-mono text-xs">{(c.started_at || '').replace('T', ' ').slice(0, 19)}</td>
                                        <td className="font-mono text-xs">{c.direction}</td>
                                        <td className="font-mono text-xs">{c.did_number || '—'}</td>
                                        <td className="font-mono text-xs">{c.caller_id || '—'}</td>
                                        <td className="font-mono text-xs">{c.destination || c.extension_id || '—'}</td>
                                        <td className="font-mono text-xs">
                                            {c.ai_intent ? (
                                                <span style={{ color: 'var(--brand)' }}>
                                                    {c.ai_intent}{c.ai_confidence ? ` ${(c.ai_confidence*100).toFixed(0)}%` : ''}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="text-center">
                                            <StatusPill status={
                                                c.status === 'answered' ? 'success' :
                                                c.status === 'missed' ? 'warning' :
                                                c.status === 'failed' ? 'error' : 'info'
                                            } label={c.status} />
                                        </td>
                                        <td className="text-right font-mono">{c.duration_seconds}s</td>
                                        <td className="text-right font-mono">
                                            {c.cost ? `${c.cost.toFixed(4)} ${c.currency}` : '—'}
                                        </td>
                                        <td className="font-mono text-[11px]">
                                            {c.external_lead_id ? (
                                                <span style={{ color: '#10B981' }}>
                                                    ↗ {c.external_lead_id.slice(0, 10)}…
                                                </span>
                                            ) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </Card>
        </>
    );
};

export default CallLogs;
